import type { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

/** Icon-chip tints — vary per card so the overview grid reads like a colorful dashboard. */
export type CardHue = "blue" | "violet" | "teal" | "amber" | "rose" | "green";

const HUE_CHIP_CLASSES: Record<CardHue, string> = {
	blue: "bg-gradient-to-br from-primary-50 to-primary-100 text-primary-600",
	violet: "bg-gradient-to-br from-accent-50 to-accent-100 text-accent-600",
	teal: "bg-gradient-to-br from-teal-50 to-teal-100 text-teal-600",
	amber: "bg-gradient-to-br from-amber-50 to-amber-100 text-amber-600",
	rose: "bg-gradient-to-br from-rose-50 to-rose-100 text-rose-600",
	green: "bg-gradient-to-br from-secondary-50 to-secondary-100 text-secondary-600",
};

/** Standard Přehled hub card: icon + title header, optional trailing element. */
export function OverviewCard({
	icon: Icon,
	title,
	trailing,
	children,
	className,
	hue = "blue",
}: {
	icon: LucideIcon;
	title: string;
	trailing?: ReactNode;
	children: ReactNode;
	className?: string;
	hue?: CardHue;
}) {
	return (
		<section className={`card-surface rounded-xl p-6 ${className ?? ""}`}>
			<div className="flex items-center gap-2.5 mb-4">
				<div
					className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${HUE_CHIP_CLASSES[hue]}`}
				>
					<Icon className="h-4 w-4" />
				</div>
				<h2 className="text-lg font-semibold">{title}</h2>
				{trailing && <span className="ml-auto shrink-0">{trailing}</span>}
			</div>
			{children}
		</section>
	);
}

/** "→ full section" footer link, relative to the company layout route. */
export function CardFooterLink({ to, children }: { to: string; children: ReactNode }) {
	return (
		<Link
			to={to}
			prefetch="intent"
			className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
		>
			{children}
			<ArrowRight className="h-3.5 w-3.5" />
		</Link>
	);
}

/** Label/value row used across the hub cards. */
export function DataRow({
	label,
	value,
	mono,
}: {
	label: string;
	value: ReactNode;
	mono?: boolean;
}) {
	return (
		<div className="flex items-baseline gap-2">
			<dt className="text-muted-foreground w-40 shrink-0">{label}</dt>
			<dd className={mono ? "font-mono" : ""}>{value}</dd>
		</div>
	);
}
